import {
  collectionFilterConditionsSchema,
  collectionFilterScopeSchema,
  normalizeCollectionFilterConditions,
  type CollectionFilterConditions,
  type CollectionFilterScope,
} from '@kiipu/app-protocol';

export const conditionKeys = ['platform', 'kind', 'status', 'quality'] as const;
export type LibrarySearch = {
  view?: string;
  folderId?: string;
  tagId?: string;
  q?: string;
  platform?: string | string[];
  status?: string | string[];
  kind?: string | string[];
  quality?: string | string[];
  item?: string;
  desktopAction?: string;
  filterSnapshot?: '1';
};

/** Shared by the router and settings return path. Route arrays are TanStack JSON arrays. */
export function parseLibrarySearch(input: Record<string, unknown>): LibrarySearch {
  const result: LibrarySearch = {};
  for (const key of ['view', 'folderId', 'tagId', 'q', 'item', 'desktopAction'] as const) {
    if (typeof input[key] === 'string' && input[key]) result[key] = input[key];
  }
  if (input.filterSnapshot === '1' || input.filterSnapshot === 1) result.filterSnapshot = '1';
  for (const key of conditionKeys) {
    const value = input[key];
    if (typeof value === 'string' && value) result[key] = value;
    else if (Array.isArray(value)) {
      const parsed = collectionFilterConditionsSchema.shape[key].safeParse(
        value.length ? value : undefined,
      );
      if (parsed.success && parsed.data) result[key] = parsed.data;
    }
  }
  return result;
}

export function conditionsFromSearch(search: LibrarySearch): CollectionFilterConditions {
  const values: Record<string, unknown> = {};
  for (const key of conditionKeys) {
    const raw = search[key];
    const parsed = collectionFilterConditionsSchema.shape[key].safeParse(
      typeof raw === 'string' ? [raw] : raw,
    );
    if (parsed.success && parsed.data) values[key] = parsed.data;
  }
  // Old clients used status for completeness as well as capture progress.
  if (
    typeof search.status === 'string' &&
    ['complete', 'partial', 'link_only'].includes(search.status) &&
    !values.quality
  )
    values.quality = [search.status];
  return normalizeCollectionFilterConditions(values);
}

export function scopeForSearch(search: LibrarySearch): CollectionFilterScope | null {
  if (search.tagId || search.view === 'trash') return null;
  const candidate = search.folderId
    ? { kind: 'folder', id: search.folderId }
    : { kind: search.view || 'all' };
  const parsed = collectionFilterScopeSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

export function hasFilterSnapshot(search: LibrarySearch) {
  return search.filterSnapshot === '1' || conditionKeys.some((key) => search[key] !== undefined);
}

export function withConditions(
  search: LibrarySearch,
  conditions: CollectionFilterConditions,
): LibrarySearch {
  const next = { ...search, filterSnapshot: '1' as const };
  for (const key of conditionKeys) delete next[key];
  return { ...next, ...normalizeCollectionFilterConditions(conditions) };
}

/** API transport deliberately uses repeated parameters, unlike the frontend route encoding. */
export function libraryQueryParams(filters: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      for (const entry of value) if (typeof entry === 'string') params.append(key, entry);
    } else if (typeof value === 'string' && value) params.set(key, value);
  }
  return params;
}

export const firstFilterValue = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;
